import { useQuery } from '@tanstack/react-query';
import type { Href } from 'expo-router';
import { useAtomValue } from 'jotai';

import { addUtmToInviteLink } from '@/constants/invites';
import { REFERRAL_BASE_URL } from '@/constants/links';
import {
  SHARE_LINK_FALLBACK_ROUTE,
  SHARE_LINK_LABELS,
  type ShareLinkKind,
  type ShareLinkLabels,
} from '@/constants/shareLink';
import { getCurrentUserScopedQueryOptions } from '@/services/getCurrentUserScoped';
import {
  actAsUserIdAtom,
  authenticatedUserIdAtom,
  authStateResolvedAtom,
} from '@/services/globalState';
import { getMyPublicLinkV2QueryOptions } from '@/services/myPublicLinkV2';

/**
 * Adds `utm_source=share_link`, falling back to the bare link when it cannot be parsed. The web
 * domain defaults to empty in some environments, and a throw here would take down the root
 * layout this resolver runs in.
 */
function taggedReferralLink(link: string): string {
  try {
    return addUtmToInviteLink(link, 'share_link');
  } catch {
    return link;
  }
}

export type OwnShareLink = {
  kind: ShareLinkKind;
  url: string;
  labels: ShareLinkLabels;
  fallbackRoute: Href;
};

/**
 * The personal link the device holder shares from the icon action, Messages and Siri, and the
 * words those surfaces show for it. Experts share their Ask Primum link; LMPs with a referral
 * code share their referral link, tagged `utm_source=share_link` as the invites screen's own
 * share tags it.
 *
 * The native surfaces hand a personal link to the device, so three guards hold before one is
 * offered:
 *
 * - The authenticated user is read, never the impersonated one: a support session acting as
 *   someone must not publish their link.
 * - A user id is required. With none, the current-user query falls back to a shared cache key
 *   that still holds the last signed-in user, which would otherwise outlive sign-out.
 * - `isAuthResolved` lets callers tell "no user yet" apart from "signed out" during the
 *   cold-load session restore; acting earlier wipes state on every relaunch.
 *
 * `kind` is known as soon as the user is, while an expert's `shareLink` waits for their link
 * to load. Reading the expert link assigns a slug on first use, so callers that only need
 * `kind` pass `withLink: false`.
 */
export function useOwnShareLink({ withLink = true }: { withLink?: boolean } = {}) {
  const authenticatedUserId = useAtomValue(authenticatedUserIdAtom);

  const actAsUserId = useAtomValue(actAsUserIdAtom);

  const isAuthResolved = useAtomValue(authStateResolvedAtom);

  const isOwnSession = Boolean(authenticatedUserId) && !actAsUserId;

  const userQueryOptions = getCurrentUserScopedQueryOptions({ userId: authenticatedUserId });

  /*
   * Every request carries X-Act-As while impersonating, so a fetch under the authenticated
   * user's key would store the impersonated profile, referral code included, as the device
   * holder's and publish it once the session ends.
   */
  const { data: user } = useQuery({
    ...userQueryOptions,
    enabled: userQueryOptions.enabled && isOwnSession,
  });

  const kind = ((): ShareLinkKind | null => {
    if (!isOwnSession || !user) {
      return null;
    }

    if (user.userType === 'expert') {
      return 'expert';
    }

    return user.userType === 'lmp' && user.referralCode ? 'referral' : null;
  })();

  const { data: expertLink, isError: isLinkError } = useQuery({
    ...getMyPublicLinkV2QueryOptions(authenticatedUserId),
    enabled: withLink && kind === 'expert',
  });

  const url = (() => {
    if (kind === 'expert') {
      return expertLink?.url ?? null;
    }

    return kind === 'referral'
      ? taggedReferralLink(`${REFERRAL_BASE_URL}${user?.referralCode}`)
      : null;
  })();

  const userName = user?.firstName && user?.lastName ? `${user.firstName} ${user.lastName}` : null;

  const shareLink: OwnShareLink | null =
    kind && url
      ? {
          kind,
          url,
          labels: SHARE_LINK_LABELS[kind],
          fallbackRoute: SHARE_LINK_FALLBACK_ROUTE[kind],
        }
      : null;

  return {
    authenticatedUserId,
    actAsUserId,
    isAuthResolved,
    user,
    userName,
    kind,
    shareLink,
    isLinkError: kind === 'expert' && isLinkError,
  };
}
